import * as React from 'react';
import classNames from 'classnames';

import EntityAvatar from 'components/entity/entity-avatar/EntityAvatar';
import EntityName from 'components/entity/entity-name/EntityName';
import MetricsChannelsBasic from 'components/metrics/channels-basic/MetricsChannelsBasic';
import Tooltip from 'components/Tooltip';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';

import { __t, applyTranslations } from 'utils/language';
import { getEntityFromProfile } from 'utils/view';
import {
  CHANNEL_TYPE_INSTAGRAM,
  CHANNEL_TYPE_TIKTOK,
  CHANNEL_TYPE_YOUTUBE
} from 'utils/channels';
import { getInfluencerChannelUrl } from 'utils/influencers';

import './creator-info-with-channels.scss';

import type { ChannelMetrics, ChannelTypes, SubcampaignInfluencerProfile } from 'types';
import type { MetricsChannelsBasicItem } from 'components/metrics/channels-basic/types';
import type { ContentCardCreatorProfile } from 'components/cards/content-card/types';

applyTranslations(require('./creator-info-with-channels.i18n.json'));

type Props = {
  creatorProfile: Partial<ContentCardCreatorProfile|SubcampaignInfluencerProfile>,
  metricExtractor?: (creatorProfile: Partial<ContentCardCreatorProfile|SubcampaignInfluencerProfile>, channel: ChannelTypes) => MetricsChannelsBasicItem,
  includeRatio?: boolean,
  includeLocation?: boolean,
  metrics?: ChannelMetrics,
  omitLinks?: boolean,
  hasInfluencerName?: boolean
};

const SHARED_MSG_KEY = 'creator_info_with_channels';
const CHANNELS: Array<ChannelTypes> = [CHANNEL_TYPE_INSTAGRAM, CHANNEL_TYPE_TIKTOK, CHANNEL_TYPE_YOUTUBE];

const CreatorInfoWithChannels = ({
  creatorProfile,
  includeRatio,
  hasInfluencerName,
  metrics,
  includeLocation,
  omitLinks,
  metricExtractor }: Props): JSX.Element => {
  const entity = getEntityFromProfile(creatorProfile, false, omitLinks);
  const metricsChannels = CHANNELS.map((channel: ChannelTypes) => {
    if (metricExtractor) {
      return metricExtractor(creatorProfile, channel);
    }

    return {
      engagementRate: (metrics || creatorProfile.channels)?.[channel]?.engagementRate ?? 0,
      followers: (metrics || creatorProfile.channels)?.[channel]?.followers ?? 0,
      type: channel,
      url: getInfluencerChannelUrl(creatorProfile, channel)
    };
  });

  const detailClasses = classNames({
    'creator-info-with-channels__details': true,
    'creator-info-with-channels__details--with-creator-name': hasInfluencerName
  });

  const getCountry = () => {
    if (creatorProfile?.country?.code) {
      return `${creatorProfile?.country?.name} (${creatorProfile?.country?.code})`;
    }

    return creatorProfile?.country?.name;
  };

  const country = getCountry();

  return (
    <div className="creator-info-with-channels">
      <div className="creator-info-with-channels__avatar">
        <Tooltip
          message={entity?.url ? __t(`${SHARED_MSG_KEY}.tooltips.view_profile`) : undefined}
          width="auto-compact"
          classNameWrapper="creator-info-with-channels__tooltip"
        >
          <EntityAvatar
            bordered
            openInNewTab
            entity={entity}
            link={entity?.url}
            customSize={{
              width: 48,
              height: 48
            }}
          />
        </Tooltip>
      </div>
      <div className={detailClasses}>
        <div className="creator-info-with-channels__name">
          <Tooltip
            message={entity?.url ? __t(`${SHARED_MSG_KEY}.tooltips.view_profile`) : undefined}
            width="auto-compact"
            classNameWrapper="creator-info-with-channels__tooltip"
          >
            <EntityName
              className="creator-info-with-channels__entity-name text-overflow-ellipsis"
              openInNewTab
              entity={entity}
              link={entity?.url}
              size="small"
              multiline
            />
          </Tooltip>
        </div>
        {
          entity && hasInfluencerName && (
            <div className="creator-info-with-channels__creator-name">
              {`${entity.firstName} ${entity.lastName}`}
            </div>
          )
        }
        <div className="creator-info-with-channels__metrics-with-countries">
          <MetricsChannelsBasic
            channels={metricsChannels}
            showChannels={CHANNELS}
            maxChannels={2}
            type="limited"
            includeRatio={includeRatio}
          />
          {
            includeLocation && country && (
              <div className="creator-info-with-channels__countries">
                <SVGDynamicIcon
                  name="drop-pin-custom-bg"
                  width={16}
                  height={16}
                  customColor="grey"
                />
                <span className="creator-info-with-channels__countries-string">
                  {country}
                </span>
              </div>
            )
          }
        </div>
      </div>
    </div>
  );
};

export default CreatorInfoWithChannels;
